// frontend/src/components/WeatherAlerts.tsx
// State-based accordion with severity prioritization

import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper,
  Box,
  Typography,
  Alert,
  Chip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Divider,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EventIcon from '@mui/icons-material/Event';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HistoryIcon from '@mui/icons-material/History';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://spatial-risk-api.onrender.com/api';

interface WeatherAlert {
  id: string;
  event: string;
  severity: string;
  headline: string;
  description: string;
  instruction?: string;
  effective: string;
  expires: string;
  areaDesc: string;
  is_active: boolean;
  fetched_at: string;
}

interface LocationAlert {
  zipCode: string;
  postalCode?: string;
  city?: string;
  state?: string;
  alerts: WeatherAlert[];
  riskScore?: number;
  assetValue?: number;
}

interface WeatherAlertsProps {
  locations: Array<{
    postalCode?: string;
    zipCode?: string;
    city?: string;
    state?: string;
    latitude?: number;
    longitude?: number;
    risk_score?: number;
    asset_value?: number | string;
  }>;
  portfolioId?: string;
}

interface StateAlertGroup {
  state: string;
  alerts: Array<{
    alert: WeatherAlert;
    affectedLocations: LocationAlert[];
  }>;
  maxSeverity: number;
}

const severityRank = (severity: string): number => {
  switch (severity?.toLowerCase()) {
    case 'extreme': return 4;
    case 'severe': return 3;
    case 'moderate': return 2;
    case 'minor': return 1;
    default: return 0;
  }
};

const getSeverityColor = (severity: string): string => {
  switch (severity?.toLowerCase()) {
    case 'extreme': return '#B71C1C';
    case 'severe': return '#E65100';
    case 'moderate': return '#F57C00';
    case 'minor': return '#FBC02D';
    default: return '#1976D2';
  }
};

const getSeverityIcon = (severity: string) => {
  switch (severity?.toLowerCase()) {
    case 'extreme':
    case 'severe':
      return <ErrorOutlineIcon />;
    default:
      return <WarningIcon />;
  }
};

const formatDateTime = (isoString: string): string => {
  try {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    return isoString;
  }
};

const getTimeRemaining = (expiresString: string): string => {
  try {
    const expires = new Date(expiresString);
    const now = new Date();
    const diff = expires.getTime() - now.getTime();
    
    if (diff < 0) return 'Expired';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d remaining`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  } catch (error) {
    return '';
  }
};

export default function WeatherAlerts({ locations, portfolioId }: WeatherAlertsProps) {
  const [alerts, setAlerts] = useState<LocationAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedAlert, setExpandedAlert] = useState<string | null>(null);
  const [showHistorical, setShowHistorical] = useState(false);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      if (portfolioId) {
        const response = await axios.get(
          `${API_URL}/alerts/portfolio/${portfolioId}`
        );
        setAlerts(response.data.alerts || []);
      } else {
        const postalCodes = locations.map(
          loc => loc.postalCode || loc.zipCode
        ).filter(Boolean);

        if (postalCodes.length === 0) {
          setAlerts([]);
          setLoading(false);
          return;
        }

        const response = await axios.post(
          `${API_URL}/alerts/check`,
          { postalCodes }
        );
        setAlerts(response.data || []);
      }
    } catch (err: any) {
      console.error('Error fetching alerts:', err);
      setError('Failed to fetch weather alerts');
    } finally {
      setLoading(false);
    }
  }, [locations, portfolioId]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  // Group alerts by state with affected locations
  const groupAlertsByState = (isActive: boolean): StateAlertGroup[] => {
    const stateMap = new Map<string, StateAlertGroup>();

    alerts.forEach(location => {
      const state = location.state || 'Unknown';
      
      location.alerts
        .filter(alert => alert.is_active === isActive)
        .forEach(alert => {
          if (!stateMap.has(state)) {
            stateMap.set(state, {
              state,
              alerts: [],
              maxSeverity: 0
            });
          }

          const stateGroup = stateMap.get(state)!;
          
          // Find or create alert entry
          let alertEntry = stateGroup.alerts.find(a => a.alert.id === alert.id);
          if (!alertEntry) {
            alertEntry = {
              alert,
              affectedLocations: []
            };
            stateGroup.alerts.push(alertEntry);
          }

          // Add this location to affected locations
          alertEntry.affectedLocations.push(location);

          // Update max severity for state
          const severity = severityRank(alert.severity);
          if (severity > stateGroup.maxSeverity) {
            stateGroup.maxSeverity = severity;
          }
        });
    });

    // Sort states by max severity (highest first)
    const sortedStates = Array.from(stateMap.values()).sort(
      (a, b) => b.maxSeverity - a.maxSeverity
    );

    // Sort alerts within each state by severity
    sortedStates.forEach(stateGroup => {
      stateGroup.alerts.sort(
        (a, b) => severityRank(b.alert.severity) - severityRank(a.alert.severity)
      );
    });

    return sortedStates;
  };

  const activeStateGroups = groupAlertsByState(true);
  const historicalStateGroups = groupAlertsByState(false);

  const totalActiveAlerts = activeStateGroups.reduce(
    (sum, state) => sum + state.alerts.length,
    0
  );

  const totalHistoricalAlerts = historicalStateGroups.reduce(
    (sum, state) => sum + state.alerts.length,
    0
  );

  if (loading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#FFFFFF' }}>
        <CircularProgress sx={{ color: '#1976D2' }} />
        <Typography variant="body2" sx={{ mt: 2, color: '#666' }}>
          Fetching live weather alerts from National Weather Service...
        </Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ bgcolor: '#F5F5F5', minHeight: '400px' }}>
      {/* Header Summary */}
      <Paper sx={{ p: 3, mb: 2, bgcolor: '#FFFFFF', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ color: '#1A1A1A', fontWeight: 600 }}>
            Weather Alerts
          </Typography>
          <IconButton onClick={fetchAlerts} size="small" sx={{ color: '#1976D2' }}>
            <HistoryIcon />
          </IconButton>
        </Box>

        {totalActiveAlerts === 0 ? (
          <Alert 
            severity="success" 
            icon={<CheckCircleOutlineIcon />}
            sx={{ 
              bgcolor: '#E8F5E9',
              color: '#1B5E20',
              '& .MuiAlert-icon': { color: '#2E7D32' }
            }}
          >
            <Typography variant="body1" fontWeight={600}>
              No Active Weather Alerts
            </Typography>
            <Typography variant="body2">
              All portfolio locations are clear of active weather threats.
            </Typography>
          </Alert>
        ) : (
          <Alert 
            severity="warning" 
            icon={<WarningIcon />}
            sx={{ 
              bgcolor: '#FFF3E0',
              color: '#E65100',
              '& .MuiAlert-icon': { color: '#F57C00' }
            }}
          >
            <Typography variant="body1" fontWeight={600}>
              {totalActiveAlerts} Active Alert{totalActiveAlerts !== 1 ? 's' : ''} Across {activeStateGroups.length} State{activeStateGroups.length !== 1 ? 's' : ''}
            </Typography>
            <Typography variant="body2">
              Expand states below to view affected areas
            </Typography>
          </Alert>
        )}
      </Paper>

      {/* ACTIVE ALERTS BY STATE */}
      {activeStateGroups.length > 0 && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: '#FFFFFF', borderRadius: 2 }}>
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#1A1A1A', 
              fontWeight: 600, 
              mb: 2,
              px: 1
            }}
          >
            Active Weather Alerts by State
          </Typography>

          {activeStateGroups.map((stateGroup, stateIndex) => {
            const highestSeverityAlert = stateGroup.alerts[0].alert;
            const alertCount = stateGroup.alerts.length;
            
            return (
              <Accordion 
                key={stateGroup.state}
                defaultExpanded={stateIndex === 0}
                sx={{
                  mb: 1,
                  border: `2px solid ${getSeverityColor(highestSeverityAlert.severity)}`,
                  '&:before': { display: 'none' },
                  boxShadow: 2
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    bgcolor: '#FAFAFA',
                    '& .MuiAccordionSummary-content': {
                      alignItems: 'center',
                      gap: 2
                    }
                  }}
                >
                  {/* State Badge */}
                  <Chip
                    label={stateGroup.state}
                    sx={{
                      bgcolor: getSeverityColor(highestSeverityAlert.severity),
                      color: '#FFFFFF',
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      minWidth: '50px'
                    }}
                  />

                  {/* Alert Count */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getSeverityIcon(highestSeverityAlert.severity)}
                    <Typography variant="body1" sx={{ color: '#1A1A1A', fontWeight: 600 }}>
                      {alertCount} Alert{alertCount !== 1 ? 's' : ''}
                    </Typography>
                  </Box>

                  {/* Severity Badge */}
                  <Chip
                    label={highestSeverityAlert.severity}
                    size="small"
                    sx={{
                      bgcolor: getSeverityColor(highestSeverityAlert.severity),
                      color: '#FFFFFF',
                      fontWeight: 600
                    }}
                  />
                </AccordionSummary>

                <AccordionDetails sx={{ p: 2, bgcolor: '#FFFFFF' }}>
                  {stateGroup.alerts.map((alertEntry, alertIndex) => (
                    <Card
                      key={`${alertEntry.alert.id}-${alertIndex}`}
                      sx={{
                        mb: 2,
                        border: `1px solid ${getSeverityColor(alertEntry.alert.severity)}`,
                        '&:last-child': { mb: 0 }
                      }}
                    >
                      <CardContent>
                        {/* Alert Header */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography 
                              variant="h6" 
                              sx={{ 
                                color: '#1A1A1A',
                                fontWeight: 700,
                                mb: 0.5
                              }}
                            >
                              {alertEntry.alert.event}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: '#424242',
                                fontWeight: 500
                              }}
                            >
                              {alertEntry.alert.headline}
                            </Typography>
                          </Box>
                          <Chip
                            label={alertEntry.alert.severity}
                            sx={{
                              bgcolor: getSeverityColor(alertEntry.alert.severity),
                              color: '#FFFFFF',
                              fontWeight: 700
                            }}
                          />
                        </Box>

                        {/* Affected Locations */}
                        <Box sx={{ 
                          p: 1.5, 
                          bgcolor: '#F5F5F5', 
                          borderRadius: 1,
                          mb: 2,
                          borderLeft: `4px solid ${getSeverityColor(alertEntry.alert.severity)}`
                        }}>
                          <Typography variant="subtitle2" sx={{ color: '#424242', fontWeight: 600, mb: 1 }}>
                            Affected Portfolio Locations ({alertEntry.affectedLocations.length}):
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {alertEntry.affectedLocations.map(loc => (
                              <Chip
                                key={loc.zipCode}
                                icon={<LocationOnIcon />}
                                label={`${loc.city || loc.zipCode}`}
                                size="small"
                                sx={{
                                  bgcolor: '#FFFFFF',
                                  border: '1px solid #E0E0E0',
                                  color: '#424242',
                                  fontWeight: 500,
                                  '& .MuiChip-icon': { color: getSeverityColor(alertEntry.alert.severity) }
                                }}
                              />
                            ))}
                          </Box>
                        </Box>

                        {/* Time Information */}
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 2, 
                          flexWrap: 'wrap', 
                          mb: 2,
                          p: 1.5,
                          bgcolor: '#FAFAFA',
                          borderRadius: 1
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <EventIcon sx={{ fontSize: 18, color: '#757575' }} />
                            <Typography variant="body2" sx={{ color: '#424242' }}>
                              <strong>Issued:</strong> {formatDateTime(alertEntry.alert.effective)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ScheduleIcon sx={{ fontSize: 18, color: '#757575' }} />
                            <Typography variant="body2" sx={{ color: '#424242' }}>
                              <strong>Expires:</strong> {formatDateTime(alertEntry.alert.expires)}
                            </Typography>
                          </Box>
                          <Chip
                            label={getTimeRemaining(alertEntry.alert.expires)}
                            size="small"
                            sx={{
                              bgcolor: getSeverityColor(alertEntry.alert.severity),
                              color: '#FFFFFF',
                              fontWeight: 700
                            }}
                          />
                        </Box>

                        {/* Description */}
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: '#424242',
                            mb: 2,
                            lineHeight: 1.7
                          }}
                        >
                          <strong style={{ color: '#1976D2' }}>Description:</strong><br />
                          {alertEntry.alert.description}
                        </Typography>

                        {/* Safety Instructions */}
                        {alertEntry.alert.instruction && (
                          <Alert 
                            severity="warning"
                            sx={{ 
                              bgcolor: '#FFF3E0',
                              color: '#E65100',
                              '& .MuiAlert-icon': { color: '#F57C00' }
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                              Safety Instructions:
                            </Typography>
                            <Typography variant="body2">
                              {alertEntry.alert.instruction}
                            </Typography>
                          </Alert>
                        )}

                        {/* Affected Area */}
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: '#757575',
                            display: 'block',
                            mt: 2,
                            fontStyle: 'italic'
                          }}
                        >
                          Affected Area: {alertEntry.alert.areaDesc}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Paper>
      )}

      {/* HISTORICAL ALERTS SECTION */}
      {totalHistoricalAlerts > 0 && (
        <Paper sx={{ p: 2, bgcolor: '#FFFFFF', borderRadius: 2 }}>
          <Accordion 
            expanded={showHistorical}
            onChange={() => setShowHistorical(!showHistorical)}
            sx={{
              border: '1px solid #E0E0E0',
              '&:before': { display: 'none' },
              boxShadow: 0
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: '#FAFAFA',
                '& .MuiAccordionSummary-content': {
                  alignItems: 'center',
                  gap: 2
                }
              }}
            >
              <HistoryIcon sx={{ color: '#757575' }} />
              <Typography variant="h6" sx={{ color: '#424242', fontWeight: 600 }}>
                Historical Alerts ({totalHistoricalAlerts})
              </Typography>
            </AccordionSummary>

            <AccordionDetails sx={{ p: 2, bgcolor: '#FAFAFA' }}>
              {historicalStateGroups.map(stateGroup => (
                <Box key={stateGroup.state} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: '#424242', fontWeight: 600, mb: 1 }}>
                    {stateGroup.state} - {stateGroup.alerts.length} Alert{stateGroup.alerts.length !== 1 ? 's' : ''}
                  </Typography>
                  <List sx={{ bgcolor: '#FFFFFF', borderRadius: 1 }}>
                    {stateGroup.alerts.map((alertEntry, index) => (
                      <ListItem 
                        key={`${alertEntry.alert.id}-${index}`}
                        sx={{
                          borderLeft: `4px solid ${getSeverityColor(alertEntry.alert.severity)}`,
                          mb: 1,
                          bgcolor: '#FFFFFF',
                          opacity: 0.7,
                          '&:last-child': { mb: 0 }
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Chip
                                label={alertEntry.alert.severity}
                                size="small"
                                sx={{
                                  bgcolor: getSeverityColor(alertEntry.alert.severity),
                                  color: '#FFFFFF',
                                  fontSize: '0.7rem',
                                  height: 20
                                }}
                              />
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#424242' }}>
                                {alertEntry.alert.event}
                              </Typography>
                              <Chip
                                label="EXPIRED"
                                size="small"
                                sx={{
                                  bgcolor: '#9E9E9E',
                                  color: '#FFFFFF',
                                  fontSize: '0.65rem',
                                  height: 18
                                }}
                              />
                            </Box>
                          }
                          secondary={
                            <Typography variant="caption" sx={{ color: '#757575' }}>
                              Issued: {formatDateTime(alertEntry.alert.effective)} • 
                              Expired: {formatDateTime(alertEntry.alert.expires)}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        </Paper>
      )}

      {/* Footer */}
      <Box sx={{ mt: 3, p: 2, textAlign: 'center' }}>
        <Typography variant="caption" sx={{ color: '#757575', fontStyle: 'italic' }}>
          Weather alert data from National Weather Service (NWS) • Last updated: {new Date().toLocaleTimeString()}
        </Typography>
      </Box>
    </Box>
  );
}